// frontend/src/pages/Payment/PaymentPage.js
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiCheck, FiCreditCard, FiLock, FiCalendar, FiShield, FiStar, FiClipboard } from 'react-icons/fi';
import {
  getSubscriptionPlans, createSubscriptionOrder,
  verifyAndRegister, offlineRegister,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useRazorpay from '../../hooks/useRazorpay';
import styles from './PaymentPage.module.css';

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

const PaymentPage = () => {
  const { loginUser }  = useAuth();
  const navigate       = useNavigate();
  const location       = useLocation();
  const formData       = location.state?.formData;
  const { openCheckout } = useRazorpay();

  const [plans, setPlans]                       = useState([]);
  const [offlineEnabled, setOfflineEnabled]     = useState(false);
  const [selectedPlan, setSelectedPlan]         = useState(null);
  const [plansLoading, setPlansLoading]         = useState(true);
  const [paying, setPaying]                     = useState(false);
  const [offlinePaying, setOfflinePaying]       = useState(false);

  useEffect(() => {
    if (!formData) { navigate('/login'); return; }
    getSubscriptionPlans(formData.email)
      .then((res) => {
        setPlans(res.plans);
        setOfflineEnabled(res.offlinePaymentEnabled || false);
        if (res.plans.length > 0) setSelectedPlan(res.plans[0]);
      })
      .catch(() => toast.error('Failed to load subscription plans'))
      .finally(() => setPlansLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePay = async () => {
    if (!selectedPlan) return toast.error('Please select a plan');
    setPaying(true);
    try {
      const orderRes = await createSubscriptionOrder({
        years: selectedPlan.years,
        planItemId: selectedPlan.id,
        email: formData.email,
      });
      const { orderId, amount, currency, key } = orderRes.data || orderRes;

      const payment = await openCheckout({
        key, amount, currency,
        name: 'JainamSevaSetu',
        description: `${selectedPlan.label} Subscription`,
        order_id: orderId,
        prefill: { name: formData.name, email: formData.email, contact: formData.phone },
        notes: { derasar_name: formData.derasarName },
        theme: { color: '#e65100' },
      });

      const regRes = await verifyAndRegister({
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_order_id:   payment.razorpay_order_id,
        razorpay_signature:  payment.razorpay_signature,
        ...formData,
        subscriptionYears: selectedPlan.years,
        planItemId: selectedPlan.id,
      });

      loginUser(regRes.data || regRes);
      toast.success('Payment successful! Registration submitted. Awaiting super admin approval.');
      navigate('/dashboard');
    } catch (err) {
      if (err.message === 'cancelled') {
        toast.info('Payment cancelled');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Payment failed. Please try again.');
      }
    } finally {
      setPaying(false);
    }
  };

  const handleOfflinePay = async () => {
    if (!selectedPlan) return toast.error('Please select a plan');
    setOfflinePaying(true);
    try {
      const regRes = await offlineRegister({
        ...formData,
        subscriptionYears: selectedPlan.years,
        planItemId: selectedPlan.id,
      });
      loginUser(regRes.data || regRes);
      toast.success('Offline registration submitted. Awaiting payment confirmation and super admin approval.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Offline registration failed.');
    } finally {
      setOfflinePaying(false);
    }
  };

  if (!formData) return null;

  return (
    <div className={styles.page}>
      <div className={styles.leftPanel}>
        <div className={styles.brandRow}>
          <img src="/icon.png" alt="Jainam Seva Setu" className={styles.brandIcon} />
          <span className={styles.brandName}>JainamSevaSetu</span>
        </div>

        <div className={styles.summaryBox}>
          <div className={styles.summaryTitle}>Registration Summary</div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Derasar Name</span>
            <span className={styles.summaryValue}>{formData.derasarName}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Location</span>
            <span className={styles.summaryValue}>{formData.derasarCity}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Admin</span>
            <span className={styles.summaryValue}>{formData.name}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Email</span>
            <span className={styles.summaryValue}>{formData.email}</span>
          </div>
        </div>

        {selectedPlan && (
          <div className={styles.orderBox}>
            <div className={styles.orderTitle}>Order Details</div>
            <div className={styles.orderRow}>
              <span>Subscription ({selectedPlan.label})</span>
              <span>{formatPrice(selectedPlan.price)}</span>
            </div>
            {selectedPlan.years > 1 && (
              <div className={styles.orderRow} style={{ fontSize: '0.82em', opacity: 0.75 }}>
                <span>{formatPrice(Math.round(selectedPlan.price / selectedPlan.years))} × {selectedPlan.years} years</span>
              </div>
            )}
            <div className={styles.orderDivider} />
            <div className={`${styles.orderRow} ${styles.orderTotal}`}>
              <span>Total</span>
              <span>{formatPrice(selectedPlan.price)}</span>
            </div>
          </div>
        )}

        <div className={styles.secureNote}>
          <FiShield size={12} /> Payments secured by Razorpay
        </div>
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.formContainer}>
          <h2 className={styles.formTitle}>Choose Subscription Plan</h2>
          <p className={styles.formSubtitle}>Select a plan, then pay securely via Razorpay</p>

          {plansLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : (
            <div className={styles.plansGrid}>
              {(() => {
                const regularPlans  = plans.filter((p) => !p.isSpecialOffer);
                const basePlan      = regularPlans.find((p) => p.years === 1);
                const baseYearlyPx  = basePlan ? basePlan.price : (regularPlans[0]?.price || 0);
                return plans.map((plan) => {
                  const perYear    = Math.round(plan.price / plan.years);
                  const savingsPct = !plan.isSpecialOffer && baseYearlyPx > 0 && plan.years > 1
                    ? Math.round((1 - perYear / baseYearlyPx) * 100) : 0;
                  return (
                    <div
                      key={plan.id || plan.years}
                      className={`${styles.planCard} ${selectedPlan?.id === plan.id ? styles.planSelected : ''} ${plan.isSpecialOffer ? styles.planSpecial : ''}`}
                      onClick={() => setSelectedPlan(plan)}
                    >
                      {plan.isSpecialOffer && (
                        <div className={styles.specialBadge}><FiStar size={10} /> Special Offer</div>
                      )}
                      <div className={styles.planYears}>{plan.label}</div>
                      <div className={styles.planPrice}>{formatPrice(plan.price)}</div>
                      <div className={styles.planPricePerYear}>{formatPrice(perYear)} / year</div>
                      {savingsPct > 0 && <div className={styles.planSavings}>Save {savingsPct}%</div>}
                      <div className={styles.planCheck}>
                        {selectedPlan?.id === plan.id && <FiCheck size={14} />}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}

          {selectedPlan && (
            <div className={styles.selectedSummary}>
              <FiCalendar size={14} style={{ marginRight: 6 }} />
              Selected: <strong>{selectedPlan.label}</strong> — {formatPrice(selectedPlan.price)}
            </div>
          )}

          <button
            className="btn btn-primary btn-block"
            style={{ marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handlePay}
            disabled={!selectedPlan || plansLoading || paying || offlinePaying}
          >
            <FiCreditCard size={16} />
            {paying ? 'Opening Razorpay…' : `Pay ${selectedPlan ? formatPrice(selectedPlan.price) : ''} via Razorpay`}
          </button>

          {offlineEnabled && (
            <button
              className="btn btn-secondary btn-block"
              style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={handleOfflinePay}
              disabled={!selectedPlan || plansLoading || paying || offlinePaying}
            >
              <FiClipboard size={16} />
              {offlinePaying ? 'Submitting…' : 'Pay via Offline'}
            </button>
          )}

          <div className={styles.razorpayNote}>
            <FiLock size={12} />
            {offlineEnabled
              ? 'Online: Razorpay secure checkout. Offline: super admin will confirm your payment manually.'
              : 'You will be redirected to Razorpay\'s secure checkout. Supports UPI, cards, net banking & wallets.'}
          </div>

          <button
            className="btn btn-ghost btn-block"
            style={{ marginTop: 8 }}
            onClick={() => navigate('/login/admin')}
            disabled={paying || offlinePaying}
            type="button"
          >
            ← Back to Registration
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
